import { addDays, addMonths, addYears, format, isValid, parse } from 'date-fns';
import { fr } from 'date-fns/locale';

function normalizeDateParam(input: string): string {
  if (!input) return '';

  const candidates = [
    { fmt: 'yyyy-MM-dd', locale: undefined },
    { fmt: 'dd/MM/yyyy', locale: fr },
    { fmt: 'dd-MM-yyyy', locale: fr }
  ] as const;

  for (const { fmt, locale } of candidates) {
    const parsed = parse(input, fmt, new Date(), { locale });
    if (isValid(parsed)) {
      return format(parsed, 'yyyy-MM-dd');
    }
  }

  const fallback = new Date(input);
  if (isValid(fallback)) {
    return format(fallback, 'yyyy-MM-dd');
  }

  return '';
}

/**
 * Builds API date range query params for these inputs:
 * - "YYYY" -> [after]=YYYY-01-01 and [strictly_before]=(YYYY+1)-01-01
 * - "YYYY-MM" -> [after]=YYYY-MM-01 and [strictly_before]=next month
 * - Full date (or parseable date) -> [after]=day and [strictly_before]=day+1
 */
export function buildApiDateRangeParams(field: string, rawInput: string): Record<string, string> {
  if (!rawInput) return {};

  if (/^\d{4}$/.test(rawInput)) {
    const start = new Date(Number(rawInput), 0, 1);
    const end = addYears(start, 1);
    return {
      [`${field}[after]`]: format(start, 'yyyy-MM-dd'),
      [`${field}[strictly_before]`]: format(end, 'yyyy-MM-dd')
    };
  }

  if (/^\d{4}-\d{2}$/.test(rawInput)) {
    const [y, m] = rawInput.split('-').map((n) => Number(n));
    const start = new Date(y, (m || 1) - 1, 1);
    const end = addMonths(start, 1);
    return {
      [`${field}[after]`]: format(start, 'yyyy-MM-dd'),
      [`${field}[strictly_before]`]: format(end, 'yyyy-MM-dd')
    };
  }

  const normalized = normalizeDateParam(rawInput);
  if (!normalized) return {};

  const start = parse(normalized, 'yyyy-MM-dd', new Date());
  if (!isValid(start)) return {};

  const end = addDays(start, 1);
  return {
    [`${field}[after]`]: format(start, 'yyyy-MM-dd'),
    [`${field}[strictly_before]`]: format(end, 'yyyy-MM-dd')
  };
}
